<template>
  <template v-if="showErrorTile">
    <ResponseTile
      :back-button-label="$t('usernamechange.error.buttonLabel')"
      :text="$t('usernamechange.error.text')"
      :title="$t('usernamechange.error.title')"
      @button:back="onCancel"
    />
    <div class="imageWrapper">
      <TeaserTechnischerFehler />
    </div>
  </template>

  <template v-else>
    <Modal
      :centeredText="false"
      :headerDivider="false"
      :image-src="`${assetsBaseUrl}/assets/images/Teaser_Hinweis.png`"
      :maxWidth="600"
      :showCloseIcon="true"
      :showModal="showEmailAddressModal"
      @closeModal="closeEmailAddressModal"
    >
      <template #content>
        <div class="usernamechange-modal__content">
          <h2>{{ $t('usernamechange.emailAddressModal.title') }}</h2>
          <p
            v-dompurify-html="
              $t('usernamechange.emailAddressModal.description', {
                email: initialEmail
              })
            "
          ></p>
        </div>
      </template>
      <template #footer>
        <div class="usernamechange-modal__actions">
          <CustomButton
            class="usernamechange-modal__button"
            @click="confirmEmailAddressModal"
            :buttonColor="ButtonColor.PRIMARY"
            :disabledState="loading"
            :pendingState="loading"
          >
            {{ $t('usernamechange.emailAddressModal.confirm') }}
          </CustomButton>
          <CustomButton
            class="usernamechange-modal__button"
            @click="closeEmailAddressModal"
            :buttonColor="ButtonColor.SECONDARY"
            :disabledState="loading"
          >
            {{ $t('usernamechange.emailAddressModal.cancel') }}
          </CustomButton>
        </div>
      </template>
    </Modal>

    <Card
      v-if="usernameChangeSucceeded"
      :title="$t('usernamechange.success.title')"
      :footerDivider="false"
    >
      <template v-slot:content>
        <p
          v-dompurify-html="
            $t('usernamechange.success.description', { remainingSecondsBeforeRedirect })
          "
        ></p>
      </template>
      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="logoutToLogin"
            buttonId="username-change.logout-button"
            :buttonColor="ButtonColor.PRIMARY"
          >
            {{ $t('usernamechange.success.buttonLogin') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <Card
      v-else
      :title="$t('usernamechange.general.title')"
      :footerDivider="false"
      :loadingState="loadingInitialEmail"
    >
      <template v-slot:infobox>
        <h3>{{ $t('usernamechange.infoBox.title') }}</h3>
        <div>{{ initialUsername }}</div>
      </template>
      <template v-slot:content>
        <div id="username">
          <BasicTextfield
            @input="onUsernameInput"
            id="activation.username"
            :label="$t('usernamechange.newUsername.label')"
            :placeholder="$t('usernamechange.newUsername.placeholder')"
            :required="true"
            :status="usernameTextfieldStatus"
            :value="username"
            inputmode="email"
            @error="toggleUsernameError"
            @update:blur="validateUsername"
          >
            <template v-slot:error>{{ usernameErrorMsg }}</template>
            <template v-slot:info>
              <div v-dompurify-html="$t('usernamechange.newUsername.info')"></div>
              <ul class="list-dots">
                <li>{{ $t('usernamechange.newUsername.validation.uppercase') }}</li>
                <li>{{ $t('usernamechange.newUsername.validation.lowercase') }}</li>
                <li>{{ $t('usernamechange.newUsername.validation.digits') }}</li>
                <li>
                  {{ $t('usernamechange.newUsername.validation.specialCharacters') }}
                </li>
                <li>{{ $t('usernamechange.newUsername.validation.length') }}</li>
              </ul>
            </template>
          </BasicTextfield>
          <BasicTextfield
            v-show="showUsernameRepeat"
            @input="onUsernameRepeatInput"
            id="activation.username.repeat"
            :label="$t('usernamechange.usernameRepeat.label')"
            :status="usernameRepeatTextfieldStatus"
            :value="usernameRepeat"
            inputmode="email"
            @keyup.enter="onSubmit"
            @update:blur="validateUsernameRepeatOnBlur"
          >
            <template v-slot:error>
              {{ $t('usernamechange.usernameRepeat.errors.notMatching') }}
            </template>
            <template v-slot:info>
              {{ $t('usernamechange.usernameRepeat.info') }}
            </template>
          </BasicTextfield>
        </div>
      </template>
      <template v-slot:footer>
        <div class="footer-cta footer-cta--left">
          <CustomButton
            @click="onSubmit"
            buttonId="username-change.weiter-button"
            :pendingState="loading"
            :disabledState="isSubmitDisabled"
            :buttonColor="ButtonColor.PRIMARY"
          >
            {{ $t('usernamechange.submit.text') }}
          </CustomButton>
          <CustomButton
            @click="onCancel"
            buttonId="username-change.abbrechen-button"
            :buttonColor="ButtonColor.LINK"
          >
            {{ $t('usernamechange.cancel.text') }}
          </CustomButton>
        </div>
      </template>
    </Card>

    <div v-if="usernameChangeSucceeded" class="imageWrapper">
      <slot name="image">
        <TeaserUsernameSuccess></TeaserUsernameSuccess>
      </slot>
    </div>
    <div v-else class="imageWrapper">
      <slot name="image">
        <TeaserUsername></TeaserUsername>
      </slot>
    </div>
  </template>
</template>

<script setup lang="ts">
import {
  BasicTextfield,
  ButtonColor,
  Card,
  CustomButton,
  InputStatus,
  Modal,
  ResponseTile
} from 'de-zurich-ui-library'
import { navigate } from '@/utils/NavigationUtils'
import { fetchAccountsByPPID, updateUsername } from '@/stores/accountManagementService'
import { ResponseError } from 'account-api'
import { getPartnerProxyId, keycloakLogout } from '@/utils/KeycloakUtils'
import { computed, onBeforeMount, onBeforeUnmount, onMounted, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import TeaserUsername from '@/componentsTenant/rahmen/images/TeaserUsername.vue'
import TeaserTechnischerFehler from '@/componentsTenant/rahmen/images/TeaserTechnischerFehler.vue'
import TeaserUsernameSuccess from '@/componentsTenant/rahmen/images/TeaserUsernameSuccess.vue'

const { t } = useI18n()

const partnerProxyId = ref<string | null>(null)
const assetsBaseUrl: string = globalThis.ENV_CONFIG.ASSETS_BASE_URL.CUSTOMER_PORTAL || ''
const initialUsername = ref('')
const initialEmail = ref('')
const accountId = ref('')
const loadingInitialEmail = ref(false)

const username = ref('')
const usernameTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const usernameErrorMsg = ref('')
const usernameRepeat = ref('')
const usernameRepeatTextfieldStatus = ref<InputStatus>(InputStatus.DEFAULT)
const usernameRegex = /^[a-zA-Z0-9_\-.+@&;!$%]{7,60}$/
const loading = ref(false)
const showEmailAddressModal = ref(false)
const showUsernameRepeat = ref(false)
const showErrorTile = ref(false)
const usernameChangeSucceeded = ref(false)
const remainingSecondsBeforeRedirect = ref(5)
const alreadyInUseUsername = ref('')
const USERNAME_ALREADY_IN_USE_CODE = 'E409-001-AMS'

let countdownInterval: ReturnType<typeof setInterval> | undefined
let logoutTimeout: ReturnType<typeof setTimeout> | undefined

const showTechnicalError = (): void => {
  showErrorTile.value = true
}

const isUsernameAlreadyInUseError = (
  status: number,
  code: string | undefined
): boolean => {
  return status === 409 && code === USERNAME_ALREADY_IN_USE_CODE
}

const isSubmitDisabled = computed(() => {
  const usernameValid = usernameTextfieldStatus.value === InputStatus.VALID
  const usernameRepeatValid = usernameRepeatTextfieldStatus.value === InputStatus.VALID
  return (
    !usernameValid ||
    !usernameRepeatValid ||
    loadingInitialEmail.value ||
    loading.value ||
    usernameChangeSucceeded.value ||
    !accountId.value
  )
})

onBeforeMount(() => {
  partnerProxyId.value = getPartnerProxyId()
})

onMounted(async () => {
  if (!partnerProxyId.value) {
    showTechnicalError()
    return
  }

  loadingInitialEmail.value = true
  try {
    const accounts = await fetchAccountsByPPID(partnerProxyId.value)
    if (accounts.length > 0) {
      const firstAccount = accounts[0]
      initialEmail.value = firstAccount?.email ?? ''
      initialUsername.value = firstAccount?.username ?? ''
      accountId.value = firstAccount?.accountId ?? ''
    } else {
      showTechnicalError()
    }
  } catch (error) {
    if (error instanceof ResponseError) {
      if (error.response.status === 401) {
        navigate('/')
      } else {
        showTechnicalError()
      }
    } else {
      showTechnicalError()
    }
  } finally {
    loadingInitialEmail.value = false
  }
})

onBeforeUnmount(() => {
  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  if (logoutTimeout) {
    clearTimeout(logoutTimeout)
  }
})

const triggerUsernameRepeat = (): void => {
  showUsernameRepeat.value = true
}

const normalizeEmail = (value: string): string => {
  return value.trim().toLowerCase()
}

const isEmailAddress = (value: string): boolean => {
  const trimmedValue = value.trim()
  const parts = trimmedValue.split('@')

  if (parts.length !== 2) {
    return false
  }

  const [localPart, domain] = parts
  if (!localPart || !domain || !domain.includes('.')) {
    return false
  }

  const lastDotIndex = domain.lastIndexOf('.')
  if (lastDotIndex <= 0) {
    return false
  }

  return domain.slice(lastDotIndex + 1).length >= 2
}

const usernameMatchesStoredEmail = (): boolean => {
  return normalizeEmail(username.value) === normalizeEmail(initialEmail.value)
}

const onUsernameInput = (val: string): void => {
  triggerUsernameRepeat()
  username.value = val
  validateUsername()
  validateUsernameRepeat()
}

const onUsernameRepeatInput = (val: string): void => {
  usernameRepeat.value = val
  validateUsernameRepeat()
}

const toggleUsernameError = (): void => {
  validateUsername()
}

const setUsernameError = (message: string): void => {
  usernameErrorMsg.value = message
  usernameTextfieldStatus.value = InputStatus.ERROR
}

const setUsernameValid = (): void => {
  usernameErrorMsg.value = ''
  usernameTextfieldStatus.value = InputStatus.VALID
}

const isUsernameFormatValid = (): boolean => {
  return usernameRegex.test(username.value)
}

const validateUsername = (): void => {
  const trimmedUsername = username.value.trim()
  const normalizedUsername = normalizeEmail(username.value)

  if (!trimmedUsername) {
    setUsernameError(String(t('usernamechange.newUsername.errors.empty')))
    return
  }

  if (
    alreadyInUseUsername.value !== '' &&
    normalizedUsername === alreadyInUseUsername.value
  ) {
    setUsernameError(String(t('usernamechange.newUsername.errors.alreadyInUse')))
    return
  }

  if (username.value.length < 7) {
    setUsernameError(
      String(
        t('usernamechange.newUsername.errors.tooShort', {
          chars: username.value.length
        })
      )
    )
  } else if (username.value.length > 60) {
    setUsernameError(
      String(
        t('usernamechange.newUsername.errors.tooLong', {
          chars: username.value.length
        })
      )
    )
  } else if (isUsernameFormatValid()) {
    setUsernameValid()
  } else {
    setUsernameError(String(t('usernamechange.newUsername.errors.rulesViolated')))
  }
}

const validateUsernameRepeat = (forceError = false): void => {
  if (!showUsernameRepeat.value && !forceError) {
    usernameRepeatTextfieldStatus.value = InputStatus.DEFAULT
    return
  }

  if (!usernameRepeat.value) {
    usernameRepeatTextfieldStatus.value = forceError
      ? InputStatus.ERROR
      : InputStatus.DEFAULT
    return
  }

  usernameRepeatTextfieldStatus.value =
    username.value === usernameRepeat.value ? InputStatus.VALID : InputStatus.ERROR
}

const validateUsernameRepeatOnBlur = (): void => {
  validateUsernameRepeat(true)
}

const validateInput = (): void => {
  validateUsername()
  validateUsernameRepeat(true)
}

const closeEmailAddressModal = (): void => {
  if (!loading.value) {
    showEmailAddressModal.value = false
  }
}

const sendUsernameChangeRequest = async (): Promise<void> => {
  loading.value = true
  try {
    await updateUsername(accountId.value, username.value)
    usernameChangeSucceeded.value = true
    startLogoutTimer()
  } catch (error) {
    if (error instanceof ResponseError) {
      const errorResponse = await error.response
        .json()
        .catch(() => null as { code?: string } | null)

      if (isUsernameAlreadyInUseError(error.response.status, errorResponse?.code)) {
        alreadyInUseUsername.value = normalizeEmail(username.value)
        usernameErrorMsg.value = String(
          t('usernamechange.newUsername.errors.alreadyInUse')
        )
        usernameTextfieldStatus.value = InputStatus.ERROR
      } else {
        showTechnicalError()
      }
    } else {
      showTechnicalError()
    }
  } finally {
    loading.value = false
  }
}

const logoutToLogin = async (): Promise<void> => {
  await keycloakLogout(globalThis.ENV_CONFIG?.REDIRECTS.LOGIN_REDIRECT)
}

const startLogoutTimer = (): void => {
  remainingSecondsBeforeRedirect.value = 5

  if (countdownInterval) {
    clearInterval(countdownInterval)
  }
  if (logoutTimeout) {
    clearTimeout(logoutTimeout)
  }

  countdownInterval = setInterval(() => {
    remainingSecondsBeforeRedirect.value--
  }, 1000)

  logoutTimeout = setTimeout(() => {
    if (countdownInterval) {
      clearInterval(countdownInterval)
    }
    void logoutToLogin()
  }, 5000)
}

const confirmEmailAddressModal = async (): Promise<void> => {
  showEmailAddressModal.value = false
  await sendUsernameChangeRequest()
}

const onSubmit = async (): Promise<void> => {
  validateInput()

  if (isSubmitDisabled.value) {
    return
  }

  if (isEmailAddress(username.value) && !usernameMatchesStoredEmail()) {
    showEmailAddressModal.value = true
    return
  }

  await sendUsernameChangeRequest()
}

const onCancel = (): void => {
  navigate(globalThis.ENV_CONFIG?.REDIRECTS.PERSONAL_PROFIL_PAGE)
}
</script>

<style lang="scss" scoped>
.usernamechange-modal__content {
  padding-top: 16px;

  h2 {
    margin-bottom: 36px;
    font-size: 30px;
  }

  p {
    margin: 0;
    line-height: 1.5;
  }
}

.usernamechange-modal__actions {
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
  align-items: center;
  justify-content: center;
  margin-top: 24px;

  .usernamechange-modal__button {
    width: min(100%, 530px);
    display: block;
  }

  :deep(.usernamechange-modal__button button) {
    width: 100%;
  }
}

:deep(.modal img) {
  max-width: 100%;
  height: auto;
  max-height: 200px;
  object-fit: contain;
}
</style>
